/**
 * Sentinel palette slots for badge art. Designer SVGs are authored against
 * four agreed sentinel colors; the conversion script maps each to one of
 * these props, and every visual state below is just a different palette.
 */
export interface BadgePalette {
  primary: string
  shade: string
  accent: string
  detail: string
}

/**
 * Earned-state palettes, indexed by tier name.
 *
 * These are built from the app's saturated brand colors so badge art holds
 * its own against the Play screen: Orange, Blue, Yellow, then Pink/Purple.
 * Each palette still supplies a deep shade and a pale highlight, preserving
 * the four-color depth designed into every badge SVG.
 */
export const TIER_PALETTES = {
  Bronze: {
    // A saturated rose-copper separates Bronze from Gold, while the pale
    // highlight and deep red-brown shadow give the flat fills metallic pop.
    primary: '#F06F4F',
    shade: '#9E3025',
    accent: '#FFD0B8',
    detail: '#4D1711'
  },
  Silver: {
    primary: '#5BC4FF',
    shade: '#167DB5',
    accent: '#BCEBFF',
    detail: '#0C4A6E'
  },
  Gold: {
    // A saturated yellow-gold clearly separates Gold from rose-copper Bronze;
    // the near-cream highlight and deep amber shadow add polished contrast.
    primary: '#FFD21F',
    shade: '#B87500',
    accent: '#FFF7A3',
    detail: '#5C3500'
  },
  Platinum: {
    primary: '#F25ACB',
    shade: '#B72C96',
    accent: '#FFC1EE',
    detail: '#7A1C65'
  }
} as const satisfies Record<string, BadgePalette>

/**
 * Unearned, grid: soft ivory-cream tones echo the app's Brand/Cream
 * (#F6F2E9), while retaining enough separation between slots for the art to
 * read faintly instead of collapsing into one flat blob.
 */
export const SILHOUETTE_PALETTE: BadgePalette = {
  primary: '#F0E9DC',
  shade: '#E2D7C7',
  accent: '#FCF8F1',
  // Keep the source's dark chip-outline role warm and light while locked.
  // Some badge designs use it for a large outer ring, not just tiny details.
  detail: '#E6DCCF'
}
